import { useState, useEffect } from 'react';
import { usePage } from '@inertiajs/react';
import { useMediaQuery } from '@mantine/hooks';
import { Link } from '@inertiajs/react';

import {
  Accordion,
  Container,
  Card,
  Button,
  Text,
  Drawer,
  Title,
  Transition,
  MantineProvider,
  Stack,
  Center,
  Flex,
  Divider,
  Grid,
  Group,
  Image,
  AspectRatio,
  Box,
  Avatar,
  ThemeIcon,
  Badge,
  Paper,
} from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { Notifications, showNotification } from '@mantine/notifications';
import {
  IconCheck,
  IconArrowRight,
  IconArrowLeft,
  IconCalendarCheck,
  IconClock,
  IconMapPin,
  IconCoin,
  IconInfoCircle,
  IconArrowBackUp,
  IconCalendarEvent,
  IconLock,
  IconX,
} from '@tabler/icons-react';
import ManageBadge from '../../components/ManageBadge';
import Layout from '../../components/Layout';
import LoginModal from '../../components/LoginModal';
import { useUser } from '../../src/context/UserContext';

import '@mantine/dates/styles.css';
import '../../styles/Calendar.css';
import '../../styles/PrivateLessonPage.css';

ShowPrivateLessonPage.useBlankLayout = true;

interface Slot {
  time: string;
  booked: boolean;
  date: string; // each slot includes a date field
  id: number;
}

interface PageProps {
  available_slots: Slot[];
  earliest_date?: string;
  earliest_time?: string;
  min_date?: string;
  max_date?: string;
  artist_name?: string;
  artist_slug?: string;
  artist_avatar_url?: string;
  event_name?: string;
  event_id?: number;
  event_image_url?: string;
  lesson_type_name?: string;
  lesson_duration?: number;
  lesson_price?: number;
  lesson_location?: string;
  lesson_description?: string;
  additional_info?: string;
  artist_page_url?: string;
  event_page_url?: string;
  edit_page_url?: string;
  manage_bookings_url?: string;
  user_has_access?: boolean;
  currentUserUsername?: string;
  private_lesson_page_slug?: string;
  [key: string]: any;
}

// Artist Details component - used in both layouts
const ArtistDetailsCard = ({ props }: { props: PageProps }) => {
  const hasEvent = !!props.event_name;
  const hasEventImage = !!props.event_image_url;
  const hasArtistAvatar = !!props.artist_avatar_url;
  const isDesktop = useMediaQuery('(min-width: 1000px)');

  return (
    <Card
      p="lg"
      radius="md"
      shadow="sm"
      style={{ backgroundColor: '#3d3c3e', minHeight: isDesktop ? '70vh' : 'auto', overflow: 'auto' }}
    >
      {/* ManageBadge Component */}
      {props.user_has_access && (
        <ManageBadge
          entityType="lesson"
          entitySlug={props.artist_slug || ''}
          userHasAccess={props.user_has_access}
          bookingsUrl={props.manage_bookings_url}
          editUrl={props.edit_page_url}
        />
      )}

      {/* Navigation Links */}
      <Group mb="md">
        <Button
          component="a"
          href={props.artist_page_url}
          variant="outline"
          size="sm"
          color="orange"
          leftSection={<IconArrowBackUp size={16} />}
        >
          Back to Artist
        </Button>

        {hasEvent && (
          <Button
            component="a"
            href={props.event_page_url}
            variant="outline"
            size="sm"
            color="orange"
            leftSection={<IconCalendarEvent size={16} />}
          >
            Go to Event
          </Button>
        )}
      </Group>

      {/* Images Section */}
      {hasEventImage ? (
        <Grid mb="lg">
          <Grid.Col span={6}>
            <AspectRatio ratio={16 / 9}>
              {hasArtistAvatar ? (
                <Image src={props.artist_avatar_url} alt={`${props.artist_name}'s avatar`} radius="md" />
              ) : (
                <Box
                  style={{
                    backgroundColor: '#2C2E33',
                    borderRadius: 'md',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text c="dimmed" ta="center">
                    No artist image
                  </Text>
                </Box>
              )}
            </AspectRatio>
          </Grid.Col>
          <Grid.Col span={6}>
            <AspectRatio ratio={16 / 9}>
              <Image src={props.event_image_url} alt={`${props.event_name} event image`} radius="md" />
            </AspectRatio>
          </Grid.Col>
        </Grid>
      ) : (
        <Center mb="sm">
          {hasArtistAvatar ? (
            <Avatar src={props.artist_avatar_url} alt={`${props.artist_name}'s avatar`} size={100} radius="md" />
          ) : (
            <Avatar size={100} radius="md" color="orange">
              {props.artist_name?.substring(0, 2).toUpperCase()}
            </Avatar>
          )}
        </Center>
      )}

      {/* Lesson Details */}
      <Title order={4} style={{ color: '#FFF7ED', marginBottom: '0.25rem' }} className="poppins">
        {props.lesson_type_name} with {props.artist_name}
      </Title>

      <Text className="mb-0" style={{ color: '#FFF7ED' }}>
        {props.lesson_description}
      </Text>

      <Accordion variant="filled" radius="md" mb="sm" className="form-accordion">
        <Accordion.Item value="details">
          <Accordion.Control icon={<IconInfoCircle size={20} />} sx={{ backgroundColor: '#4d4c4e', color: '#FFF7ED' }}>
            <Text fw={500}>Private Lesson Details</Text>
          </Accordion.Control>
          <Accordion.Panel bg="#3d3c3e">
            <Stack gap="0.25rem" mt="xs">
              {hasEvent && (
                <Group wrap="nowrap" m="0">
                  <ThemeIcon color="orange" size="md" radius="xl">
                    <IconCalendarEvent size={16} />
                  </ThemeIcon>
                  <Text className="my-0 flex-start" style={{ color: '#FFF7ED', margin: 0 }}>
                    {props.event_name}
                  </Text>
                </Group>
              )}

              <Group wrap="nowrap">
                <ThemeIcon color="orange" size="md" radius="xl">
                  <IconClock size={16} />
                </ThemeIcon>
                <Text style={{ color: '#FFF7ED' }}>{props.lesson_duration} minutes</Text>
              </Group>

              <Group wrap="nowrap">
                <ThemeIcon color="orange" size="md" radius="xl">
                  <IconMapPin size={16} />
                </ThemeIcon>
                <Text style={{ color: '#FFF7ED' }}>{props.lesson_location}</Text>
              </Group>

              <Group wrap="nowrap">
                <ThemeIcon color="orange" size="md" radius="xl">
                  <IconCoin size={16} />
                </ThemeIcon>
                <Text style={{ color: '#FFF7ED' }}>${props.lesson_price}</Text>
              </Group>

              {props.additional_info && (
                <Group wrap="nowrap" align="flex-start">
                  <ThemeIcon color="orange" size="md" radius="xl">
                    <IconInfoCircle size={16} />
                  </ThemeIcon>
                  <Text className="my-0" style={{ color: '#FFF7ED' }}>
                    {props.additional_info}
                  </Text>
                </Group>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Card>
  );
};

export default function ShowPrivateLessonPage() {
  const pageProps = usePage<PageProps>().props;
  const { user } = useUser();
  const isLoggedIn = !!user;
  const isMobile = useMediaQuery('(max-width: 600px)');

  console.log('Page Props:', {
    private_lesson_page_slug: pageProps.private_lesson_page_slug,
    artist_slug: pageProps.artist_slug,
  });

  const isDesktop = useMediaQuery('(min-width: 1000px)');

  // Login Modal state
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  // Set initial date to earliest available date
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Derive isDateSelected from selectedDate
  const isDateSelected = selectedDate !== null;

  // Set initial time slot to earliest available time
  const [selectedSlot, setSelectedSlot] = useState<string | null>(() => {
    if (
      pageProps.earliest_time &&
      !pageProps.available_slots.find(
        (slot) => slot.date === pageProps.earliest_date && slot.time === pageProps.earliest_time && slot.booked,
      )
    ) {
      return pageProps.earliest_time;
    }
    return null;
  });

  // Store selected time slot id
  const [selectedSlotId, setSelectedSlotId] = useState<number | null>(null);

  // For desktop: controls which content is shown in the right column
  const [rightView, setRightView] = useState<'timeSlots' | 'details'>('timeSlots');

  // For mobile: separate drawers for time slots and private details
  const [mobileTimeSlotsOpen, setMobileTimeSlotsOpen] = useState(false);
  const [mobileDetailsOpen, setMobileDetailsOpen] = useState(false);

  // Booking submission state
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getStatusText = (status: number) => {
    switch (status) {
      case 0:
        return 'Pending';
      case 1:
        return 'Approved';
      case 3:
        return 'Completed';
      default:
        return 'Pending';
    }
  };

  const getStatusColor = (status: number): string => {
    switch (status) {
      case 0:
        return 'yellow';
      case 1:
        return 'blue';
      case 3:
        return 'green';
      default:
        return 'yellow';
    }
  };

  useEffect(() => {
    if (pageProps.earliest_date) {
      // This just ensures the date picker shows the right month
      // without actually selecting the date
      const earliestDate = parseLocalDate(pageProps.earliest_date);
      // Don't call setSelectedDate here to avoid triggering drawer
    }

    // Check if we need to redirect from login
    const redirectPath = sessionStorage.getItem('redirectAfterLogin');
    if (redirectPath) {
      // Clear the stored path after handling the redirect
      sessionStorage.removeItem('redirectAfterLogin');

      // If we're returning from login, try to restore the previously selected date and time
      if (pageProps.earliest_date) {
        setSelectedDate(parseLocalDate(pageProps.earliest_date));
      }
    }
  }, [pageProps.earliest_date]);

  // When a date is selected, update the views accordingly
  useEffect(() => {
    if (selectedDate) {
      if (isDesktop) {
        setRightView('timeSlots');
      } else {
        setMobileTimeSlotsOpen(true);
      }
    } else {
      // Reset when no date is selected
      setRightView('timeSlots');
      setMobileTimeSlotsOpen(false);
      setMobileDetailsOpen(false);
      setSelectedSlot(null);
      setSelectedSlotId(null);
    }
  }, [selectedDate, isDesktop]);

  const handleSlotSelect = (time: string, slotId: number) => {
    setSelectedSlot((prev) => (prev === time ? null : time));
    setSelectedSlotId((prev) => (prev === slotId ? null : slotId));
  };

  function parseLocalDate(yyyyMmDd: string) {
    const [year, month, day] = yyyyMmDd.split('-');
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  function toLocalDateString(date: Date) {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  const selectedDateString = selectedDate ? toLocalDateString(selectedDate) : null;
  const filteredSlots = selectedDateString
    ? pageProps.available_slots.filter((slot) => slot.date === selectedDateString)
    : [];
  const availableDates = new Set(pageProps.available_slots.map((slot) => slot.date));

  // Function to handle booking submission
  const handleBookSubmit = async () => {
    if (!isLoggedIn) {
      // Show login modal if user is not logged in
      setLoginModalOpen(true);

      // Store the current URL in sessionStorage for redirect after login
      sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
      return;
    }

    if (!selectedSlotId) {
      showNotification({
        title: 'Error',
        message: 'Please select a time slot first.',
        color: 'red',
      });
      return;
    }

    setIsSubmitting(true);

    // Extract path parts from URL
    const urlPath = window.location.pathname;
    const pathParts = urlPath.split('/').filter((part) => part);

    // From the actual routes:
    // /users/:user_username/:artist_slug/pl/:private_lesson_page_slug/private_lesson_bookings

    // The current URL is /:artist_slug/pl/:lesson_slug
    const artistSlug = pathParts[0]; // artist_slug
    const lessonSlug = pathParts[2]; // lesson_slug

    // For the user_username, we'll use the same value as artist_slug since we don't have it readily available
    // This assumes the artist owns their private lesson page
    const userUsername = artistSlug;

    // Construct the exact endpoint based on the Rails routes
    const endpoint = `/users/${userUsername}/${artistSlug}/pl/${lessonSlug}/private_lesson_bookings`;

    console.log('Using fetch with the exact route structure:');
    console.log('Current URL path:', urlPath);
    console.log('User username:', userUsername);
    console.log('Artist slug:', artistSlug);
    console.log('Lesson slug:', lessonSlug);
    console.log('Final endpoint:', endpoint);
    console.log('Selected slot ID:', selectedSlotId);

    try {
      // Get the CSRF token
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

      // Make the fetch request
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          Accept: 'application/json',
        },
        body: JSON.stringify({
          private_lesson_booking: {
            time_slot_id: selectedSlotId,
            notes: '',
          },
        }),
        credentials: 'same-origin', // Include cookies for authentication
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Response error:', errorData);
        throw new Error(errorData.message || 'Failed to create booking');
      }

      // Show success notification
      showNotification({
        title: 'Private Lesson Request Sent!',
        message: `Your request for ${selectedSlot} on ${selectedDate?.toDateString()} has been submitted and is pending approval.`,
        color: 'green',
        icon: <IconCheck size={16} />,
        position: 'top-right',
        autoClose: false,
        zIndex: 9999,
      });

      // Refresh the page after a short delay to show updated slot status
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Error creating booking:', error);

      // Show error notification
      showNotification({
        title: 'Booking Failed',
        message: 'There was an error processing your booking. Please try again.',
        color: 'red',
        icon: <IconX size={16} />,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ----- Components ----- //

  // DatePicker card (common to both desktop and mobile)
  const DatePickerCard = (
    <Card style={{ backgroundColor: '#3d3c3e', minHeight: isDesktop ? '70vh' : 'auto', overflow: 'auto' }}>
      <Center>
        <Title order={4} style={{ color: '#FFF7ED' }} className="poppins">
          Select a Date
        </Title>
      </Center>
      <Center mt="md" mb="lg">
        <DatePicker
          size="lg"
          minDate={new Date(pageProps.min_date || '')}
          maxDate={new Date(pageProps.max_date || '')}
          defaultDate={selectedDate || (pageProps.earliest_date ? parseLocalDate(pageProps.earliest_date) : undefined)}
          value={selectedDate}
          onChange={setSelectedDate}
          excludeDate={(date) => {
            const dateString = toLocalDateString(date);
            return !availableDates.has(dateString);
          }}
          weekendDays={[]}
          classNames={{ day: 'my-day-class' }}
        />
      </Center>
    </Card>
  );

  // Time Slots component (used on desktop and in the mobile drawer)
  const TimeSlotsComponent = (
    <Card
      style={{
        backgroundColor: '#3d3c3e',
        height: '70vh',
        overflow: 'auto',
      }}
    >
      <Flex align="center" mb="md">
        {/* For mobile time slots drawer, include a back button */}
        {!isDesktop && (
          <Button variant="filled" color="gray" mb="xl" size="lg" onClick={() => setMobileTimeSlotsOpen(false)}>
            <IconArrowLeft size={24} />
            &nbsp;Back
          </Button>
        )}
      </Flex>
      <Title order={4} style={{ color: '#FFF7ED', marginBottom: '1rem' }} className="poppins">
        {selectedDate
          ? `Time Slots for ${selectedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}`
          : 'Select a Date'}
      </Title>
      <Stack gap="xs">
        {filteredSlots.length > 0 ? (
          filteredSlots.map((slot, index) => {
            const isSelected = selectedSlot === slot.time && selectedSlotId === slot.id;
            return (
              <Flex key={index} gap="xs" align="center" style={{ width: '100%', marginBottom: '0.5rem' }}>
                <div
                  style={{
                    flex: 1,
                    width: isSelected ? '50%' : '100%',
                    transition: 'width 300ms ease-in-out',
                    overflow: 'hidden',
                  }}
                >
                  <Button
                    fullWidth
                    variant={isSelected ? 'filled' : 'light'}
                    color={slot.booked ? 'violet' : 'orange'}
                    disabled={slot.booked}
                    onClick={() => handleSlotSelect(slot.time, slot.id)}
                    size="lg"
                    style={{
                      ...(slot.booked && {
                        backgroundColor: '#cccccc25',
                        color: '#f2f2f280',
                        borderColor: '#f2f2f280',
                        '&:hover': {
                          backgroundColor: '#261a14',
                        },
                      }),
                    }}
                  >
                    {slot.booked && <IconCalendarCheck size={24} style={{ marginRight: '8px' }} />}
                    {slot.booked ? ` ${slot.time} - Booked` : slot.time}
                  </Button>
                </div>
                <Transition
                  mounted={isSelected}
                  transition="slide-right"
                  duration={300}
                  timingFunction="ease-in-out"
                  keepMounted
                >
                  {(transitionStyles) => (
                    <div
                      style={{
                        ...transitionStyles,
                        flex: 1,
                        width: isSelected ? '50%' : '0%',
                        overflow: 'hidden',
                        transition: 'width 300ms ease-in-out',
                      }}
                    >
                      <Button
                        fullWidth
                        variant="gradient"
                        gradient={{ from: '#EA580C', to: '#EA580C', deg: 180 }}
                        onClick={() => {
                          if (isDesktop) {
                            setRightView('details');
                          } else {
                            setMobileTimeSlotsOpen(false);
                            setMobileDetailsOpen(true);
                          }
                        }}
                        size="lg"
                        rightSection={<IconArrowRight size={20} />}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </Transition>
              </Flex>
            );
          })
        ) : (
          <Text style={{ color: '#FFF7ED' }}>No available slots for the selected date.</Text>
        )}
      </Stack>
    </Card>
  );

  // Private Details component (used on desktop and in the mobile drawer)
  const PrivateDetailsComponent = (
    <Card
      style={{
        backgroundColor: '#3d3c3e',
        height: '70vh',
        overflow: 'auto',
      }}
    >
      <Flex align="center" mb="md">
        <Button
          variant="filled"
          color="gray"
          size="lg"
          mb="xl"
          onClick={() => {
            if (isDesktop) {
              setRightView('timeSlots');
            } else {
              setMobileDetailsOpen(false);
              setMobileTimeSlotsOpen(true);
            }
          }}
        >
          <IconArrowLeft size={24} />
          &nbsp;Back
        </Button>
      </Flex>
      <Title order={4} style={{ color: '#FFF7ED', marginBottom: '1rem' }} className="poppins">
        Confirm Booking
      </Title>
      <Text size="lg" fw={500} style={{ color: '#FFF7ED' }}>
        You're requesting a private lesson at:
      </Text>
      <Text size="lg" fw={500} style={{ color: '#FFF7ED', marginBottom: '1rem' }}>
        {selectedSlot} on {selectedDate?.toDateString()}
      </Text>

      <Button fullWidth mt="md" color="orange" size="lg" loading={isSubmitting} onClick={handleBookSubmit}>
        Schedule Private Lesson
      </Button>
    </Card>
  );

  // ----- Layouts ----- //

  // Desktop layout: adaptively display two or three columns with transitions.
  // Desktop Layout (inside your return statement)
  const DesktopLayout = (
    <Flex gap="md" align="stretch">
      {/* Artist Column */}
      <Box style={{ width: '38%' }}>
        <ArtistDetailsCard props={pageProps} />
      </Box>

      {/* Calendar Column */}
      <Box style={{ width: isDateSelected ? '31%' : '62%', transition: 'width 300ms ease-in-out' }}>
        {DatePickerCard}
      </Box>

      {/* Time Slots / Details Column */}
      <Box
        style={{
          width: isDateSelected ? '31%' : '0%',
          overflow: 'hidden',
          transition: 'width 300ms ease-in-out',
        }}
      >
        {isDateSelected && (rightView === 'timeSlots' ? TimeSlotsComponent : PrivateDetailsComponent)}
      </Box>
    </Flex>
  );

  // Mobile layout: stacked Artist and Calendar
  const MobileLayout = (
    <Stack gap="xs" style={{ width: '100%' }}>
      {/* Artist Details */}
      <ArtistDetailsCard props={pageProps} />

      {/* Calendar */}
      <Card className="m-0" style={{ backgroundColor: '#3d3c3e', height: 'auto' }}>
        {DatePickerCard}
      </Card>
    </Stack>
  );

  return (
    <MantineProvider>
      <Notifications />
      {/* User Booking Alert - only show if user is logged in and has a booking */}
      {isLoggedIn && pageProps.user_booking && (
        <Box mb="md">
          <Paper
            p="md"
            radius="md"
            style={{
              backgroundColor: '#75757550',
              maxWidth: '100%',
              margin: '1rem',
            }}
          >
            <Group gap="apart" align="center">
              <div className="lesson-notify">
                <Group>
                  <Badge color={getStatusColor(pageProps.user_booking.status)} size={isMobile ? 'sm' : 'md'}>
                    {pageProps.user_booking.status_text}
                  </Badge>
                  <Text fw={700} c="#EA580C">
                    Your Private Lesson
                  </Text>
                </Group>
                <Text className="text-white">
                  You have a {pageProps.user_booking.status_text.toLowerCase()} private lesson on{' '}
                  {new Date(pageProps.user_booking.date).toLocaleDateString()} at {pageProps.user_booking.time}
                </Text>
              </div>
              <Button
                component={Link}
                href={`/users/${pageProps.currentUserUsername}/my-lessons`}
                variant="filled"
                color="orange"
                size={isMobile ? 'xs' : 'sm'}
                className="poppins pl-notify-button"
                style={{ marginLeft: 'auto' }}
              >
                View Lessons
              </Button>
            </Group>
          </Paper>
        </Box>
      )}
      <Container size="xl">{isDesktop ? DesktopLayout : MobileLayout}</Container>

      {/* Mobile Drawers */}
      {!isDesktop && (
        <>
          {/* Time Slots Drawer */}
          <Drawer
            opened={mobileTimeSlotsOpen}
            onClose={() => setMobileTimeSlotsOpen(false)}
            title="Available Time Slots"
            padding="xl"
            className="poppins"
            size="md"
            position="right"
            styles={{
              header: { display: 'flex', alignItems: 'center', backgroundColor: '#3d3c3e', color: '#f2f2f2' },
              content: { backgroundColor: '#3d3c3e', color: '#f2f2f2' },
            }}
          >
            {TimeSlotsComponent}
          </Drawer>

          {/* Private Details Drawer */}
          <Drawer
            opened={mobileDetailsOpen}
            onClose={() => {
              setMobileDetailsOpen(false);
              setMobileTimeSlotsOpen(true);
            }}
            size="md"
            position="right"
            styles={{
              header: { display: 'flex', alignItems: 'center', backgroundColor: '#3d3c3e', color: '#f2f2f2' },
              content: { backgroundColor: '#3d3c3e', color: '#f2f2f2' },
            }}
          >
            {PrivateDetailsComponent}
          </Drawer>
        </>
      )}

      {/* Login Modal */}
      <LoginModal
        opened={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        returnPath={encodeURIComponent(window.location.pathname)}
        actionText="to book this private lesson"
        entityName="private lesson"
      />
    </MantineProvider>
  );
}

// At the bottom of your ShowPrivateLessonPage.tsx file
ShowPrivateLessonPage.layout = (page: React.ReactNode) => {
  return <div style={{ width: '100%', minHeight: '100vh' }}>{page}</div>;
};
